import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { flows } from '@repletoryreach/sdk-api';
import { Stack, Group, Text, Title, Card, Badge, Button } from '@repletoryreach/sdk-ui';
import { Alert, EmptyState } from '@repletoryreach/sdk-ui/data-display';
import { Select, Switch } from '@repletoryreach/sdk-ui/inputs';
import { useT } from '../../../i18n/I18nProvider';
import { formatRelative } from '../../../lib/format';
import { HttpCallDrawer } from './HttpCallDrawer';
import './calls.css';

const MUTED = 'var(--rr-color-text-muted)';

/**
 * API calls (19 October 2026): every call the business's flows made to other systems in the last 48 hours, newest
 * first, with what was sent and what came back one click away. Keys and passwords are hidden; the log deletes itself.
 */
export default function FlowCalls() {
  const t = useT();
  const [flowId, setFlowId] = useState('');
  const [failed, setFailed] = useState(false);
  const [before, setBefore] = useState<string[]>([]);
  const [open, setOpen] = useState<string | null>(null);

  const all = useQuery({ queryKey: ['flows', 'names'], queryFn: () => flows.listFlows(), staleTime: 60_000 });
  const page = before.length > 0 ? before[before.length - 1] : undefined;
  const calls = useQuery({
    queryKey: ['http-logs', flowId, failed, page],
    queryFn: () => flows.listHttpLogs({ flow_id: flowId || undefined, failed: failed || undefined, before: page, limit: 50 }),
    retry: false
  });
  const rows = calls.data?.data ?? [];
  const flowOptions = [{ value: '', label: t('flows.calls.allFlows') }, ...((all.data?.data ?? []).map((f) => ({ value: f.id, label: f.name })))];

  return (
    <Stack gap="md" data-testid="flow-calls">
      <Stack gap={2}>
        <Title order={3}>{t('flows.calls.title')}</Title>
        <Text role="bodySmall" c={MUTED}>{t('flows.calls.intro')}</Text>
      </Stack>
      <Card withBorder>
        <Stack gap="sm">
          <Group gap="md" align="flex-end" wrap="wrap">
            <Select label={t('flows.calls.flow')} value={flowId} onChange={(e) => { setFlowId(e.currentTarget.value); setBefore([]); }} data={flowOptions} style={{ minWidth: 220 }} />
            <Switch label={t('flows.calls.failedOnly')} checked={failed} onChange={(on) => { setFailed(on); setBefore([]); }} />
          </Group>
          {calls.error && <Alert color="error" role="alert">{(calls.error as Error).message}</Alert>}
          {!calls.isPending && !calls.error && rows.length === 0 && (
            <EmptyState title={t('flows.calls.empty')} description={t('flows.calls.emptyHint')} />
          )}
          <div>
            {rows.map((c) => (
              <button key={c.id} type="button" className="rr-calls__row" data-testid="flow-call" onClick={() => setOpen(c.id)}>
                <Text role="caption" c={MUTED}>{formatRelative(c.created_at)}</Text>
                <Badge color={c.ok ? 'green' : 'red'} variant="light">{c.status === 0 ? t('flows.calls.noAnswer') : c.status}</Badge>
                <span className="rr-calls__where">
                  <span><strong>{c.method}</strong> {c.url}</span>
                  <span style={{ color: MUTED, fontSize: 12 }}>
                    {[c.flow_name, c.node_name || c.node_id, c.from_cache ? t('flows.calls.fromCache') : null, c.error].filter(Boolean).join(' · ')}
                  </span>
                </span>
                <Text role="caption" c={MUTED}>{t('flows.calls.took', { ms: c.duration_ms })}</Text>
              </button>
            ))}
          </div>
          <Group justify="space-between">
            <Button size="xs" variant="default" disabled={before.length === 0} onClick={() => setBefore(before.slice(0, -1))}>{t('flows.calls.newer')}</Button>
            <Text role="caption" c={MUTED}>{t('flows.calls.kept')}</Text>
            <Button size="xs" variant="default" disabled={rows.length < 50} onClick={() => setBefore([...before, rows[rows.length - 1].created_at])}>{t('flows.calls.older')}</Button>
          </Group>
        </Stack>
      </Card>
      <HttpCallDrawer callId={open} onClose={() => setOpen(null)} />
    </Stack>
  );
}
